import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { supabase } from '../config/supabase';
import '../assets/styles/header.css';

/**
 * Auth state for the account entry point, read straight from the auth client —
 * the sanctioned exception to "all data goes through supabaseService".
 *
 * The name comes from `user_metadata.full_name` (set at sign-up) rather than the
 * `profiles` table: a database round trip on every page load to render one word
 * isn't worth it.
 *
 * Lives here rather than in its own module because the header, the footer and
 * any other entry point all need the same three states, and this file is the
 * primary consumer.
 *
 * Returns `status`: 'loading' | 'signed-out' | 'signed-in'. Callers must keep a
 * stable box for all three so the header doesn't jump when the session resolves.
 */
export function useAccountSession() {
  const [state, setState] = useState({ status: 'loading', firstName: '' });

  useEffect(() => {
    let active = true;

    const apply = (session) => {
      if (!active) return;
      if (!session) {
        setState({ status: 'signed-out', firstName: '' });
        return;
      }
      const fullName = String(session.user?.user_metadata?.full_name || '').trim();
      setState({ status: 'signed-in', firstName: fullName.split(/\s+/)[0] || '' });
    };

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => apply(session))
      // A failed session read is indistinguishable from not being signed in,
      // and either way the account page itself handles it.
      .catch(() => apply(null));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => apply(session),
    );

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  return state;
}

/**
 * Visible label + accessible name for an account link.
 * Signed out reads as an invitation, never as a gate — booking stays open to guests.
 */
export function accountLinkText({ status, firstName }) {
  if (status === 'signed-in') {
    return {
      label: firstName || 'My account',
      ariaLabel: firstName ? `My account, ${firstName}` : 'My account',
    };
  }
  if (status === 'signed-out') {
    return { label: 'Sign in', ariaLabel: 'Sign in to your account' };
  }
  return { label: '', ariaLabel: 'Your account' };
}

export default function LogoHeader() {
  const { pathname } = useLocation();
  const isActive = (path) => pathname === path;

  const account = useAccountSession();
  const { label, ariaLabel } = accountLinkText(account);

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo-link" aria-label="Beauty by Viktoria — Home">
          <img
            src="/images/reducedPics/navbar_logo_transparent.webp"
            alt="Beauty by Viktoria"
            className="header-logo"
          />
        </Link>
        <nav className="header-nav" aria-label="Main navigation">
          <Link to="/" className={isActive('/') ? 'nav-active' : ''}>Home</Link>
          <Link to="/price" className={isActive('/price') ? 'nav-active' : ''}>Prices</Link>
          <Link to="/shop" className={isActive('/shop') ? 'nav-active' : ''}>Shop</Link>
          {/* Same destination in every state — /account is its own sign-in surface,
              so the link never has to wait for the session to resolve. */}
          <Link
            to="/account"
            className={[
              'nav-account',
              account.status === 'signed-in' ? 'is-signed-in' : '',
              isActive('/account') ? 'nav-active' : '',
            ].filter(Boolean).join(' ')}
            aria-label={ariaLabel}
            aria-current={isActive('/account') ? 'page' : undefined}
          >
            <UserRound size={16} strokeWidth={1.75} aria-hidden="true" />
            <span className="nav-account-label">
              {account.status === 'loading'
                ? <span className="nav-account-placeholder" aria-hidden="true" />
                : label}
            </span>
          </Link>
          <Link to="/book" className={`nav-book${isActive('/book') ? ' nav-active' : ''}`}>Book Now</Link>
        </nav>
      </div>
    </header>
  );
}
